import { useState } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inputCls =
  "rounded-xl border border-line bg-cream/50 px-3.5 py-2.5 text-sm text-ink outline-none transition-colors focus:border-rose";

export default function MenuEditor({ location, items, onChanged }) {
  const [draft, setDraft] = useState({ label: "", url: "" });
  const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order);

  const patch = async (id, payload) => {
    try {
      await api.patch(`/admin/menu/${id}`, payload);
      onChanged();
    } catch {
      toast.error("Could not update the link.");
    }
  };

  const move = async (index, dir) => {
    const next = [...sorted];
    const j = index + dir;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    try {
      await api.post("/admin/menu", { ordered_ids: next.map((m) => m.id) });
      onChanged();
    } catch {
      toast.error("Could not reorder links.");
    }
  };

  const add = async () => {
    if (!draft.label.trim() || !draft.url.trim()) {
      toast.error("Enter both a label and a link.");
      return;
    }
    try {
      await api.post("/admin/menu", { location, label: draft.label.trim(), url: draft.url.trim() });
      setDraft({ label: "", url: "" });
      onChanged();
    } catch {
      toast.error("Could not add the link.");
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove the "${item.label}" link?`)) return;
    try {
      await api.delete(`/admin/menu/${item.id}`);
      onChanged();
    } catch {
      toast.error("Could not delete the link.");
    }
  };

  return (
    <div data-testid={`menu-editor-${location}`}>
      <p className="mb-5 text-sm text-[#5C5C5C]">
        {location === "header"
          ? "Top navigation menu. Use #home, #shop, #categories, #about, #contact for homepage sections, /p/... for pages, or full URLs."
          : "Footer Quick Links. Link to #sections, /p/pages (e.g. /p/shipping-policy) or full URLs. You can add links like 'Track Your Order' here later."}
      </p>

      <div className="flex flex-col gap-3">
        {sorted.map((item, i) => (
          <div key={item.id} data-testid={`menu-row-${item.id}`} className={`flex flex-wrap items-center gap-2 rounded-2xl border border-line bg-white p-3.5 ${!item.visible ? "opacity-60" : ""}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" data-testid={`menu-up-${item.id}`} className="p-1 text-[#5C5C5C] hover:text-rose disabled:opacity-30"><ArrowUp size={13} /></button>
              <button onClick={() => move(i, 1)} disabled={i === sorted.length - 1} aria-label="Move down" data-testid={`menu-down-${item.id}`} className="p-1 text-[#5C5C5C] hover:text-rose disabled:opacity-30"><ArrowDown size={13} /></button>
            </div>
            <input
              data-testid={`menu-label-${item.id}`}
              defaultValue={item.label}
              onBlur={(e) => e.target.value !== item.label && patch(item.id, { label: e.target.value })}
              aria-label="Link label"
              className={`${inputCls} w-36`}
            />
            <input
              data-testid={`menu-url-${item.id}`}
              defaultValue={item.url}
              onBlur={(e) => e.target.value !== item.url && patch(item.id, { url: e.target.value })}
              aria-label="Link URL"
              className={`${inputCls} min-w-40 flex-1`}
            />
            <label className="flex cursor-pointer items-center gap-2 text-xs font-medium">
              <input
                data-testid={`menu-visible-${item.id}`}
                type="checkbox"
                checked={item.visible}
                onChange={(e) => patch(item.id, { visible: e.target.checked })}
                className="h-4 w-4 accent-[#C48B81]"
              />
              Visible
            </label>
            <button data-testid={`menu-delete-${item.id}`} onClick={() => remove(item)} aria-label={`Delete ${item.label}`} className="flex h-9 w-9 items-center justify-center rounded-full border border-line text-[#5C5C5C] transition-colors hover:border-red-400 hover:text-red-500">
              <Trash2 size={14} />
            </button>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-2 rounded-2xl border border-dashed border-line bg-white/60 p-4">
        <input
          data-testid={`menu-new-label-${location}`}
          value={draft.label}
          onChange={(e) => setDraft((d) => ({ ...d, label: e.target.value }))}
          placeholder="Link name"
          className={`${inputCls} w-40`}
        />
        <input
          data-testid={`menu-new-url-${location}`}
          value={draft.url}
          onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
          placeholder="#shop or /p/... or https://..."
          className={`${inputCls} min-w-52 flex-1`}
        />
        <button data-testid={`menu-add-${location}`} onClick={add} className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-2.5 text-xs font-medium text-cream transition-colors hover:bg-rose hover:text-white">
          <Plus size={13} /> Add Link
        </button>
      </div>
      <p className="mt-3 text-[11px] text-[#5C5C5C]">Edits save automatically when you click outside a field.</p>
    </div>
  );
}
